<template>
  <div class="rb-ob-form rb-match-page rb-session-page">
    <header>
      <h1 class="rb-ob-title">{{ $t('intake.onlineSession.reconnectTitle') }}</h1>
    </header>

    <div class="rb-session-reconnect-panel">
      <div class="rb-session-wait-pulse" aria-hidden="true"></div>
      <p>{{ $t('intake.onlineSession.reconnectBody') }}</p>
    </div>

    <ul class="rb-session-meta" :aria-label="$t('intake.onlineSession.reconnectMetaAria')">
      <li>
        <span class="rb-session-meta-k">{{ $t('intake.onlineSession.reconnectConn') }}</span>
        <span class="rb-session-meta-v">{{ connLabel }}</span>
      </li>
      <li>
        <span class="rb-session-meta-k">{{ $t('intake.onlineSession.reconnectAv') }}</span>
        <span class="rb-session-meta-v">{{ sessionTime }}</span>
      </li>
    </ul>

    <div class="rb-ob-actions">
      <button class="rb-ob-submit" type="button" :disabled="refreshing" @click="retry">
        {{ refreshing ? $t('intake.portal.loading') : $t('intake.onlineSession.reconnectRetry') }}
      </button>
      <button
          v-if="joinUrl"
          class="rb-ob-submit rb-ob-submit-secondary"
          type="button"
          @click="openJoin"
      >
        {{ $t('intake.onlineSession.openJoinLink') }}
      </button>
      <button class="rb-ob-submit rb-ob-submit-secondary" type="button" @click="support">
        {{ $t('intake.onlineSession.reconnectSupport') }}
      </button>
      <button class="rb-ob-submit rb-ob-submit-secondary" type="button" @click="safeLeave">
        {{ $t('intake.onlineSession.reconnectLeave') }}
      </button>
      <p class="rb-ob-status" role="status" aria-live="polite">{{ formStatus }}</p>
      <div class="rb-ob-trust">
        <p>{{ $t('intake.trustPath') }}</p>
        <p>{{ $t('authUi.familyNote') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  formatSessionListTitle,
  hasAuthToken,
  refreshJoinableSession,
  resolveJoinableSession,
  sessionJoinUrl,
  requestSessionAdmission,
  admissionJoinUrl,
  createActiveCareSupportCase,
} from '@/intake/portalApi'

export default {
  name: 'SessionReconnectStep',
  data() {
    return {
      refreshing: false,
      session: null,
      formStatus: '',
    }
  },
  computed: {
    lang() {
      return this.$route.params.lang || 'fa'
    },
    joinUrl() {
      return sessionJoinUrl(this.session)
    },
    sessionTime() {
      return formatSessionListTitle(this.session, this.lang) || this.$t('intake.onlineSession.reconnectAvValue')
    },
    connLabel() {
      if (this.joinUrl) return this.$t('intake.onlineSession.roomConnLinked')
      return this.$t('intake.onlineSession.reconnectConnValue')
    },
  },
  mounted() {
    resolveJoinableSession(this.$route.query.id).then((session) => {
      this.session = session
    })
  },
  methods: {
    retry() {
      this.refreshing = true
      this.formStatus = ''
      const preferred = this.$route.query.id || (this.session && this.session.id)
      const loader = hasAuthToken()
        ? refreshJoinableSession(preferred)
        : resolveJoinableSession(preferred)
      loader
          .then((session) => {
            this.session = session
            if (!session || !session.can_enter) throw new Error('SESSION_NOT_READY')
            return requestSessionAdmission(session.id).then((admission) => {
              const url = admissionJoinUrl(admission)
              if (!url) throw new Error('SESSION_JOIN_CREDENTIAL_UNAVAILABLE')
              window.open(url, '_blank', 'noopener')
              this.$router.push({
                name: 'intakeSessionRoom',
                params: {lang: this.lang},
                query: {id: String(session.id)},
              })
            })
          })
          .catch(() => {
            this.formStatus = this.$t('intake.onlineSession.reconnectFail')
          })
          .finally(() => {
            this.refreshing = false
          })
    },
    openJoin() {
      if (!this.session || !this.session.id) return
      requestSessionAdmission(this.session.id).then((admission) => {
        const url = admissionJoinUrl(admission)
        if (!url) throw new Error('SESSION_JOIN_CREDENTIAL_UNAVAILABLE')
        window.open(url, '_blank', 'noopener')
      }).catch((e) => { this.formStatus = e.message || 'SESSION_ADMISSION_DENIED' })
    },
    support() {
      createActiveCareSupportCase('ACCESS', 'Client requested help reconnecting to a session.').then(() => {
        this.formStatus = this.$t('intake.onlineSession.supportStatus')
      }).catch((e) => { this.formStatus = e.message || this.$t('intake.portal.loadError') })
    },
    safeLeave() {
      this.$router.push({name: 'portalHome', params: {lang: this.lang}})
    },
  },
}
</script>
